import { useMemo, useState, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock, DollarSign, Loader2, Users, Gauge, ChevronUp, Download, Upload, FileText, CheckCircle2, AlertCircle, ImageIcon, X, Camera, Search, SlidersHorizontal } from "lucide-react";
import { CapacityPanel } from "./CapacityPanel";

type ServiceForm = {
  name: string;
  description: string;
  durationMinutes: number;
  price: string;
  category: string;
};

type CommissionCostForm = {
  catalogItemId: string;
  deductionType: "fixed" | "percentage";
  deductionValue: string;
};

type ParsedService = {
  name: string;
  description?: string | null;
  durationMinutes?: number | null;
  price?: string | null;
  category?: string | null;
};

const emptyForm: ServiceForm = { name: "", description: "", durationMinutes: 60, price: "0.00", category: "" };
const emptyCommissionCostForm: CommissionCostForm = { catalogItemId: "manual", deductionType: "fixed", deductionValue: "" };

const normalizeSearchText = (value: unknown) => String(value ?? "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .trim();

const IMPORT_CHUNK_SIZE = 200;

// ─── XLSX helpers ─────────────────────────────────────────────────────────────

async function exportServicesToXLSX(services: any[], filename: string) {
  const XLSX = await import("xlsx");
  const rows = services.map((s: any) => ({
    "Nome": s.name,
    "Descrição": s.description ?? "",
    "Duração (min)": s.durationMinutes,
    "Preço (R$)": Number(s.price).toFixed(2).replace(".", ","),
    "Categoria": s.category ?? "",
  }));
  const ws = XLSX.utils.json_to_sheet(rows, {
    header: ["Nome", "Descrição", "Duração (min)", "Preço (R$)", "Categoria"],
  });
  ws["!cols"] = [{ wch: 30 }, { wch: 40 }, { wch: 14 }, { wch: 12 }, { wch: 20 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Serviços");
  XLSX.writeFile(wb, filename.replace(".csv", ".xlsx"));
}

async function downloadServiceTemplate() {
  const XLSX = await import("xlsx");
  const example = [
    { "Nome (*obrigatório)": "Corte Feminino", "Descrição": "Corte e finalização", "Duração (min)": 60, "Preço (R$)": "80,00", "Categoria": "Cabelo" },
    { "Nome (*obrigatório)": "Escova Progressiva", "Descrição": "Alisamento com escova", "Duração (min)": 120, "Preço (R$)": "250,00", "Categoria": "Cabelo" },
    { "Nome (*obrigatório)": "Manicure", "Descrição": "Unhas das mãos", "Duração (min)": 45, "Preço (R$)": "35,00", "Categoria": "Unhas" },
    { "Nome (*obrigatório)": "Bronzeamento", "Descrição": "Sessão de bronzeamento", "Duração (min)": 30, "Preço (R$)": "60,00", "Categoria": "Bronze" },
  ];
  const ws = XLSX.utils.json_to_sheet(example, {
    header: ["Nome (*obrigatório)", "Descrição", "Duração (min)", "Preço (R$)", "Categoria"],
  });
  ws["!cols"] = [{ wch: 30 }, { wch: 40 }, { wch: 14 }, { wch: 12 }, { wch: 20 }];

  const instructions = [
    { "Campo": "Nome (*obrigatório)", "Descrição": "Nome do serviço. Obrigatório.", "Exemplo": "Corte Feminino" },
    { "Campo": "Descrição", "Descrição": "Descrição opcional do serviço.", "Exemplo": "Corte e finalização" },
    { "Campo": "Duração (min)", "Descrição": "Duração em minutos (número inteiro). Padrão: 60.", "Exemplo": "60" },
    { "Campo": "Preço (R$)", "Descrição": "Preço com vírgula ou ponto decimal.", "Exemplo": "80,00" },
    { "Campo": "Categoria", "Descrição": "Categoria do serviço (texto livre).", "Exemplo": "Cabelo" },
    { "Campo": "", "Descrição": "", "Exemplo": "" },
    { "Campo": "REGRAS", "Descrição": "Serviços com mesmo nome serão atualizados. Máximo: 1.000 serviços por importação.", "Exemplo": "" },
  ];
  const wsInstr = XLSX.utils.json_to_sheet(instructions, {
    header: ["Campo", "Descrição", "Exemplo"],
  });
  wsInstr["!cols"] = [{ wch: 28 }, { wch: 60 }, { wch: 25 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Modelo");
  XLSX.utils.book_append_sheet(wb, wsInstr, "Instruções");
  XLSX.writeFile(wb, "modelo_importacao_servicos.xlsx");
}

function parseCSV(text: string): Array<Record<string, string>> {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(";").map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());
  return lines.slice(1).map((line) => {
    const values = line.split(";").map((v) => v.trim().replace(/^"|"$/g, ""));
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = values[i] ?? ""; });
    return row;
  });
}

function normalizeServiceRow(row: Record<string, string>): ParsedService {
  const name = row["nome (*obrigatório)"] || row["nome"] || row["name"] || "";
  const durationRaw = row["duração (min)"] || row["duracao (min)"] || row["duration"] || row["durationminutes"] || "";
  const priceRaw = row["preço (r$)"] || row["preco (r$)"] || row["preço"] || row["price"] || "";
  return {
    name,
    description: row["descrição"] || row["descricao"] || row["description"] || null,
    durationMinutes: durationRaw ? parseInt(durationRaw, 10) || null : null,
    price: priceRaw ? priceRaw.replace(",", ".") : null,
    category: row["categoria"] || row["category"] || null,
  };
}

// ─── Image Upload Helper ──────────────────────────────────────────────────────

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Service Image Upload Button ─────────────────────────────────────────────

function ServiceImageUpload({ serviceId, currentImageUrl, onUploaded }: {
  serviceId: number;
  currentImageUrl?: string | null;
  onUploaded: (url: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadMutation = (trpc as any).services.uploadImage.useMutation({
    onSuccess: (data: any) => {
      toast.success("Foto do serviço atualizada!");
      onUploaded(data.imageUrl);
    },
    onError: (e: any) => toast.error("Erro ao enviar foto: " + e.message),
  });

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Imagem muito grande (máx. 5MB)"); return; }
    const mimeType = file.type as "image/jpeg" | "image/png" | "image/webp";
    if (!["image/jpeg", "image/png", "image/webp"].includes(mimeType)) {
      toast.error("Formato inválido. Use JPG, PNG ou WebP.");
      return;
    }
    const base64 = await fileToBase64(file);
    uploadMutation.mutate({ serviceId, base64, mimeType });
    e.target.value = "";
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="relative w-full h-32 rounded-lg overflow-hidden border border-border/50 bg-muted/30 cursor-pointer group"
        onClick={() => fileRef.current?.click()}
      >
        {currentImageUrl ? (
          <>
            <img src={currentImageUrl} alt="Foto do serviço" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Camera className="w-6 h-6 text-white" />
            </div>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground group-hover:text-primary transition-colors">
            {uploadMutation.isPending ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <ImageIcon className="w-6 h-6" />
                <span className="text-xs">Clique para adicionar foto</span>
              </>
            )}
          </div>
        )}
        {uploadMutation.isPending && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} />
      {currentImageUrl && (
        <p className="text-xs text-muted-foreground">Clique na imagem para trocar</p>
      )}
    </div>
  );
}

// ─── Import Modal ─────────────────────────────────────────────────────────────

function ImportModal({ open, onClose, onSuccess }: { open: boolean; onClose: () => void; onSuccess: () => void }) {
  const [parsed, setParsed] = useState<ParsedService[]>([]);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();

  const importMutation = (trpc as any).services.import.useMutation({
    onError: (e: any) => toast.error(e.message),
  });

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    setParsed([]);
    const file = e.target.files?.[0];
    if (!file) return;
    const isXLSX = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
    const isCSV = file.name.endsWith(".csv");
    if (!isXLSX && !isCSV) { setError("Apenas arquivos .xlsx, .xls ou .csv são aceitos."); return; }

    if (isXLSX) {
      try {
        const XLSX = await import("xlsx");
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: "array" });
        const sheetName = wb.SheetNames.find((n: string) => !n.toLowerCase().includes("instru")) ?? wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        const rawRows: Record<string, string>[] = XLSX.utils.sheet_to_json(ws, { defval: "" });
        const rows = rawRows.map((r: Record<string, string>) => {
          const normalized: Record<string, string> = {};
          for (const k of Object.keys(r)) normalized[k.toLowerCase().trim()] = String(r[k]).trim();
          return normalized;
        });
        if (rows.length === 0) { setError("Planilha vazia ou sem dados válidos."); return; }
        const items = rows.map(normalizeServiceRow).filter((s) => s.name.trim());
        if (items.length === 0) { setError("Nenhum serviço com nome válido encontrado. Verifique a coluna 'Nome'."); return; }
        setParsed(items);
      } catch {
        setError("Erro ao ler o arquivo XLSX. Verifique se o arquivo não está corrompido.");
      }
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target?.result as string;
        const rows = parseCSV(text).map((r) => {
          const normalized: Record<string, string> = {};
          for (const k of Object.keys(r)) normalized[k.toLowerCase().trim()] = String(r[k]).trim();
          return normalized;
        });
        if (rows.length === 0) { setError("Arquivo CSV vazio ou sem dados válidos."); return; }
        const items = rows.map(normalizeServiceRow).filter((s) => s.name.trim());
        if (items.length === 0) { setError("Nenhum serviço com nome válido encontrado."); return; }
        setParsed(items);
      };
      reader.readAsText(file, "UTF-8");
    }
  };

  const handleImport = async () => {
    if (parsed.length === 0) return;
    const total = parsed.length;
    let totalInserted = 0;
    let totalUpdated = 0;
    let totalSkipped = 0;
    setProgress({ done: 0, total });
    try {
      for (let i = 0; i < parsed.length; i += IMPORT_CHUNK_SIZE) {
        const chunk = parsed.slice(i, i + IMPORT_CHUNK_SIZE);
        const result = await importMutation.mutateAsync({ items: chunk });
        totalInserted += result.inserted;
        totalUpdated += result.updated;
        totalSkipped += result.skipped;
        setProgress({ done: Math.min(i + IMPORT_CHUNK_SIZE, total), total });
      }
      toast.success(`Importação concluída: ${totalInserted} inseridos, ${totalUpdated} atualizados, ${totalSkipped} ignorados.`);
      utils.services.listAdmin.invalidate();
      onSuccess();
      onClose();
      setParsed([]);
    } catch (e: any) {
      toast.error(e.message ?? "Erro ao importar serviços.");
    } finally {
      setProgress(null);
    }
  };

  const isImporting = progress !== null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setParsed([]); setError(""); } }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Importar Serviços
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
            <div>
              <p className="text-sm font-medium">Modelo de planilha</p>
              <p className="text-xs text-muted-foreground">Baixe o modelo para preencher corretamente</p>
            </div>
            <Button variant="outline" size="sm" onClick={downloadServiceTemplate}>
              <FileText className="h-4 w-4 mr-2" />
              Baixar modelo (.xlsx)
            </Button>
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">Selecionar arquivo (.xlsx ou .csv)</Label>
            <div
              className="border-2 border-dashed border-border/50 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Clique para selecionar ou arraste o arquivo aqui</p>
              <p className="text-xs text-muted-foreground/60 mt-1">.xlsx, .xls ou .csv</p>
            </div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {parsed.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                {parsed.length} serviços encontrados
              </div>
              <div className="max-h-48 overflow-y-auto border border-border/50 rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Nome</TableHead>
                      <TableHead className="text-xs">Duração</TableHead>
                      <TableHead className="text-xs">Preço</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsed.slice(0, 20).map((s, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs py-1.5">{s.name}</TableCell>
                        <TableCell className="text-xs py-1.5">{s.durationMinutes ?? 60} min</TableCell>
                        <TableCell className="text-xs py-1.5">a partir de R$ {Number(s.price ?? 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    {parsed.length > 20 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-xs text-center text-muted-foreground py-2">
                          ... e mais {parsed.length - 20} serviços
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {progress && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Importando... {progress.done}/{progress.total}
              </p>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); setParsed([]); setError(""); }}>Cancelar</Button>
          <Button onClick={handleImport} disabled={parsed.length === 0 || isImporting}>
            {isImporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {isImporting ? `${Math.round(((progress?.done ?? 0) / (progress?.total ?? 1)) * 100)}%` : `Importar ${parsed.length > 0 ? `${parsed.length} serviços` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AdminServices() {
  const utils = trpc.useUtils();
  const { data: services, isLoading } = trpc.services.listAdmin.useQuery();
  const { data: professionals = [] } = trpc.professionals.listAdmin.useQuery();
  const { data: professionalsMap = {} } = (trpc as any).services.getAllProfessionalsMap.useQuery();
  const { data: commissionCostCatalog = [] } = (trpc as any).services.listCommissionCostCatalog.useQuery();
  const { data: commissionCostRules = [] } = (trpc as any).services.listCommissionCostRules.useQuery();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<ServiceForm>(emptyForm);
  const [selectedProfIds, setSelectedProfIds] = useState<number[]>([]);
  const [capacityOpen, setCapacityOpen] = useState<number | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  // Para upload de imagem no modal de edição
  const [editImageUrl, setEditImageUrl] = useState<string | null>(null);
  const [costDialogService, setCostDialogService] = useState<any | null>(null);
  const [commissionCostForm, setCommissionCostForm] = useState<CommissionCostForm>(emptyCommissionCostForm);
  const [serviceSearch, setServiceSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const commissionCostByService = useMemo(
    () => new Map((commissionCostRules as any[]).map((rule: any) => [rule.serviceId, rule])),
    [commissionCostRules],
  );
  const serviceCategories = useMemo(
    () => Array.from(new Set((services ?? []).map((service: any) => String(service.category ?? "").trim()).filter(Boolean)))
      .sort((left, right) => left.localeCompare(right, "pt-BR")),
    [services],
  );
  const filteredServices = useMemo(() => {
    const terms = normalizeSearchText(serviceSearch).split(/\s+/).filter(Boolean);
    return (services ?? []).filter((service: any) => {
      const searchable = normalizeSearchText(`${service.name} ${service.description ?? ""} ${service.category ?? ""}`);
      const matchesSearch = terms.every((term) => searchable.includes(term));
      const category = String(service.category ?? "").trim();
      const matchesCategory = categoryFilter === "all"
        || (categoryFilter === "uncategorized" ? !category : category === categoryFilter);
      const matchesStatus = statusFilter === "all"
        || (statusFilter === "active" ? service.isActive : !service.isActive);
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [services, serviceSearch, categoryFilter, statusFilter]);
  const hasServiceFilters = Boolean(serviceSearch) || categoryFilter !== "all" || statusFilter !== "active";
  const clearServiceFilters = () => {
    setServiceSearch("");
    setCategoryFilter("all");
    setStatusFilter("active");
  };

  const createMutation = trpc.services.create.useMutation({
    onSuccess: async (_, vars) => {
      await utils.services.listAdmin.invalidate();
      const fresh = await utils.services.listAdmin.fetch();
      const created = fresh?.find((s: any) => s.name === vars.name);
      if (created && selectedProfIds.length > 0) {
        setProfMutation.mutate({ serviceId: created.id, professionalIds: selectedProfIds });
      } else {
        (utils as any).services.getAllProfessionalsMap.invalidate();
      }
      toast.success("Serviço criado!");
      setDialogOpen(false);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const updateMutation = trpc.services.update.useMutation({
    onSuccess: () => {
      if (editId !== null) {
        setProfMutation.mutate({ serviceId: editId, professionalIds: selectedProfIds });
      }
      toast.success("Serviço atualizado!");
      utils.services.listAdmin.invalidate();
      setDialogOpen(false);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const setProfMutation = (trpc as any).services.setProfessionals.useMutation({
    onSuccess: () => (utils as any).services.getAllProfessionalsMap.invalidate(),
    onError: (e: any) => toast.error("Erro ao salvar profissionais: " + e.message),
  });

  const deleteMutation = trpc.services.delete.useMutation({
    onSuccess: () => { toast.success("Serviço removido!"); utils.services.listAdmin.invalidate(); },
    onError: (e: any) => toast.error(e.message),
  });

  const commissionCostMutation = (trpc as any).services.upsertCommissionCostRule.useMutation({
    onSuccess: () => {
      toast.success("Custo da comissão salvo. Ele será aplicado somente em novos atendimentos concluídos.");
      (utils as any).services.listCommissionCostRules.invalidate();
      setCostDialogService(null);
    },
    onError: (e: any) => toast.error(e.message),
  });
  const deleteCommissionCostMutation = (trpc as any).services.deleteCommissionCostRule.useMutation({
    onSuccess: () => {
      toast.success("Custo de comissão removido deste serviço.");
      (utils as any).services.listCommissionCostRules.invalidate();
      setCostDialogService(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setSelectedProfIds([]);
    setEditImageUrl(null);
    setDialogOpen(true);
  };

  const openEdit = (s: any) => {
    if (!s) return;
    setEditId(s.id);
    setForm({
      name: s.name,
      description: s.description ?? "",
      durationMinutes: s.durationMinutes,
      price: String(s.price),
      category: s.category ?? "",
    });
    const linked = (professionalsMap as Record<number, number[]>)[s.id] ?? [];
    setSelectedProfIds(linked);
    setEditImageUrl(s.imageUrl ?? null);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) return toast.error("Nome é obrigatório");
    if (editId) {
      updateMutation.mutate({ id: editId, ...form });
    } else {
      createMutation.mutate(form);
    }
  };

  const openCommissionCostDialog = (service: any) => {
    const rule = commissionCostByService.get(service.id);
    setCostDialogService(service);
    setCommissionCostForm(rule
      ? {
          catalogItemId: rule.catalogItemId ? String(rule.catalogItemId) : "manual",
          deductionType: rule.deductionType,
          deductionValue: String(Number(rule.deductionValue)),
        }
      : emptyCommissionCostForm,
    );
  };

  const saveCommissionCost = () => {
    if (!costDialogService) return;
    const value = Number(commissionCostForm.deductionValue.replace(",", "."));
    if (!Number.isFinite(value) || value <= 0) {
      toast.error("Informe um valor de custo maior que zero.");
      return;
    }
    if (commissionCostForm.deductionType === "percentage" && value > 100) {
      toast.error("O percentual deve ficar entre 0% e 100%.");
      return;
    }
    commissionCostMutation.mutate({
      serviceId: costDialogService.id,
      catalogItemId: commissionCostForm.catalogItemId === "manual" ? null : Number(commissionCostForm.catalogItemId),
      deductionType: commissionCostForm.deductionType,
      deductionValue: value,
    });
  };

  const toggleProf = (profId: number) => {
    setSelectedProfIds((prev) =>
      prev.includes(profId) ? prev.filter((id: any) => id !== profId) : [...prev, profId]
    );
  };

  const handleExport = () => {
    if (!services || services.length === 0) { toast.error("Nenhum serviço para exportar."); return; }
    exportServicesToXLSX(services, `servicos_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`${services.length} serviços exportados!`);
  };

  const isPending = createMutation.isPending || updateMutation.isPending || setProfMutation.isPending;

  const capacityService = capacityOpen !== null
    ? (services ?? []).find((s: any) => s.id === capacityOpen) ?? null
    : null;

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-[Cormorant_Garamond,serif] font-semibold">Serviços</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Gerencie os serviços oferecidos pelo salão</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload className="w-4 h-4 mr-1.5" /> Importar
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!services || services.length === 0}>
            <Download className="w-4 h-4 mr-1.5" /> Exportar
          </Button>
          <Button onClick={openCreate} className="bg-primary text-primary-foreground shrink-0">
            <Plus className="w-4 h-4 mr-1" /> Novo Serviço
          </Button>
        </div>
      </div>

      {!isLoading && (
        <Card className="border-border/60 bg-muted/[0.18] shadow-none">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={serviceSearch}
                  onChange={(event) => setServiceSearch(event.target.value)}
                  placeholder="Buscar por nome, categoria ou descrição..."
                  className="h-10 bg-background pl-9 pr-10"
                  aria-label="Buscar serviços"
                />
                {serviceSearch && (
                  <button
                    type="button"
                    onClick={() => setServiceSearch("")}
                    aria-label="Limpar busca de serviços"
                    className="absolute right-2 top-1/2 rounded p-1 -translate-y-1/2 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                  <SlidersHorizontal className="h-3.5 w-3.5" /> Filtros
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="h-10 w-full bg-background sm:w-[170px]" aria-label="Filtrar serviços por categoria">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as categorias</SelectItem>
                    {serviceCategories.map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}
                    <SelectItem value="uncategorized">Sem categoria</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-10 w-full bg-background sm:w-[145px]" aria-label="Filtrar serviços por situação">
                    <SelectValue placeholder="Situação" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativos</SelectItem>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="inactive">Inativos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
              <span>{filteredServices.length} {filteredServices.length === 1 ? "serviço encontrado" : "serviços encontrados"}</span>
              {hasServiceFilters && (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={clearServiceFilters}>
                  Limpar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredServices.length === 0 && (
            <Card className="border-dashed border-border/70 shadow-none">
              <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
                <Search className="h-7 w-7 text-muted-foreground/60" />
                <p className="font-medium">Nenhum serviço encontrado</p>
                <p className="max-w-sm text-sm text-muted-foreground">Tente outro nome, categoria ou termo da descrição.</p>
                {hasServiceFilters && <Button variant="outline" size="sm" onClick={clearServiceFilters}>Limpar busca e filtros</Button>}
              </CardContent>
            </Card>
          )}
          {filteredServices.map((service: any) => {
            const linkedProfIds = (professionalsMap as Record<number, number[]>)[service.id] ?? [];
            const linkedProfs = professionals.filter((p: any) => linkedProfIds.includes(p.id));
            const isCapacityOpen = capacityOpen === service.id;
            const commissionCostRule = commissionCostByService.get(service.id);
            return (
              <Card key={service.id} className="border-border/50 hover:border-primary/30 transition-all">
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-start gap-3">
                    {/* Thumbnail do serviço */}
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-primary/10 flex items-center justify-center shrink-0 border border-border/30">
                      {service.imageUrl ? (
                        <img src={service.imageUrl} alt={service.name} className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-primary/40" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <h3 className="font-semibold text-foreground text-sm">{service.name}</h3>
                            {!service.isActive && <Badge variant="secondary" className="text-xs">Inativo</Badge>}
                            {service.category && <Badge variant="outline" className="text-xs">{service.category}</Badge>}
                          </div>
                          {service.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{service.description}</p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {service.durationMinutes} min
                            </span>
                            {commissionCostRule && (
                              <span className="flex items-center gap-1 text-amber-700 dark:text-amber-300">
                                <DollarSign className="w-3 h-3" /> Custo: {commissionCostRule.deductionType === "percentage"
                                  ? `${Number(commissionCostRule.deductionValue).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}% da comissão`
                                  : `R$ ${Number(commissionCostRule.deductionValue).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
                              </span>
                            )}
                          </div>
                          {linkedProfs.length > 0 ? (
                            <div className="flex flex-wrap items-center gap-1 mt-1.5">
                              <Users className="w-3 h-3 text-muted-foreground shrink-0" />
                              {linkedProfs.map((p: any) => (
                                <span
                                  key={p.id}
                                  className="inline-flex items-center gap-1 text-[10px] bg-secondary/60 text-secondary-foreground rounded-full px-2 py-0.5"
                                >
                                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color ?? "#C9A96E" }} />
                                  {p.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-[10px] text-muted-foreground/60 mt-1 flex items-center gap-1">
                              <Users className="w-3 h-3" /> Todos os profissionais
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-sm font-semibold text-primary whitespace-nowrap">
                            a partir de R$ {Number(service.price).toFixed(2).replace(".", ",")}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 ${commissionCostRule ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground"}`}
                            title="Configurar custo descontado da comissão"
                            onClick={() => openCommissionCostDialog(service)}
                          >
                            <DollarSign className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 ${isCapacityOpen ? "text-primary" : "text-muted-foreground"}`}
                            title="Configurar capacidade por hora"
                            onClick={() => setCapacityOpen(isCapacityOpen ? null : service.id)}
                          >
                            <Gauge className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={() => openEdit(service)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => deleteMutation.mutate({ id: service.id })}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {(!services || services.length === 0) && (
            <div className="text-center py-12 text-muted-foreground">
              <p className="mb-3">Nenhum serviço cadastrado.</p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <Button onClick={openCreate} variant="outline">Adicionar primeiro serviço</Button>
                <Button onClick={() => setImportOpen(true)} variant="outline">
                  <Upload className="w-4 h-4 mr-1" /> Importar planilha
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Painel de capacidade por hora ─── */}
      {capacityOpen !== null && capacityService !== null && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">
                Limite de atendimentos por hora — {capacityService.name}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 ml-auto"
                onClick={() => setCapacityOpen(null)}
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </Button>
            </div>
            <CapacityPanel serviceId={capacityOpen} services={services ?? []} />
          </CardContent>
        </Card>
      )}

      {/* ─── Import Modal ─── */}
      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onSuccess={() => utils.services.listAdmin.invalidate()} />

      <Dialog open={costDialogService !== null} onOpenChange={(open) => { if (!open) setCostDialogService(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Cormorant_Garamond,serif]">
              Custo descontado da comissão
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-muted/50 px-3 py-2">
              <p className="text-xs text-muted-foreground">Serviço</p>
              <p className="text-sm font-semibold">{costDialogService?.name}</p>
            </div>
            <div>
              <Label className="text-sm">Item de custo da tabela</Label>
              <Select
                value={commissionCostForm.catalogItemId}
                onValueChange={(value) => {
                  const item = (commissionCostCatalog as any[]).find((entry: any) => String(entry.id) === value);
                  setCommissionCostForm({
                    ...commissionCostForm,
                    catalogItemId: value,
                    deductionValue: item && commissionCostForm.deductionType === "fixed" ? String(Number(item.fixedCost)) : commissionCostForm.deductionValue,
                  });
                }}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione o custo" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="manual">Valor informado manualmente</SelectItem>
                  {(commissionCostCatalog as any[]).map((item: any) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.name}{item.brand ? ` · ${item.brand}` : ""} — R$ {Number(item.fixedCost).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Ao selecionar um item, o custo em reais é preenchido automaticamente.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Tipo de desconto</Label>
                <Select
                  value={commissionCostForm.deductionType}
                  onValueChange={(value: "fixed" | "percentage") => setCommissionCostForm({ ...commissionCostForm, deductionType: value })}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                    <SelectItem value="percentage">Percentual da comissão</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">{commissionCostForm.deductionType === "percentage" ? "Percentual (%)" : "Valor (R$)"}</Label>
                <Input
                  className="mt-1"
                  type="number"
                  min={0}
                  max={commissionCostForm.deductionType === "percentage" ? 100 : undefined}
                  step="0.01"
                  value={commissionCostForm.deductionValue}
                  onChange={(event) => setCommissionCostForm({ ...commissionCostForm, deductionValue: event.target.value })}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground rounded-md border border-border/60 p-2.5">
              O desconto será aplicado somente em novas comissões geradas para este serviço. Ele nunca reduzirá a comissão abaixo de zero e não altera registros históricos.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            {commissionCostByService.get(costDialogService?.id) && (
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => deleteCommissionCostMutation.mutate({ serviceId: costDialogService.id })}
                disabled={deleteCommissionCostMutation.isPending}
              >
                Remover custo
              </Button>
            )}
            <Button variant="outline" onClick={() => setCostDialogService(null)}>Cancelar</Button>
            <Button onClick={saveCommissionCost} disabled={commissionCostMutation.isPending}>
              {commissionCostMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Salvar custo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Create/Edit Dialog ─── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90dvh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-[Cormorant_Garamond,serif]">
              {editId ? "Editar Serviço" : "Novo Serviço"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 overflow-y-auto flex-1 pr-1">
            {/* Upload de foto — só mostra ao editar */}
            {editId !== null && (
              <div>
                <Label className="text-sm flex items-center gap-1.5 mb-2">
                  <Camera className="w-3.5 h-3.5" />
                  Foto do Serviço
                </Label>
                <ServiceImageUpload
                  serviceId={editId}
                  currentImageUrl={editImageUrl}
                  onUploaded={(url) => {
                    setEditImageUrl(url);
                    utils.services.listAdmin.invalidate();
                  }}
                />
              </div>
            )}

            <div>
              <Label className="text-sm">Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Corte Feminino" className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">Descrição</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descreva o serviço..." className="mt-1 resize-none" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Duração (min)</Label>
                <Input type="number" min={15} max={480} value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">Preço (R$)</Label>
                <Input type="number" min={0} step={0.01} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Categoria</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex: Cabelo, Bronze, Estética..." className="mt-1" />
            </div>

            <div>
              <Label className="text-sm flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                Profissionais que atendem este serviço
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                Deixe em branco para permitir todos os profissionais.
              </p>
              {professionals.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Nenhum profissional cadastrado.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto pr-1">
                  {professionals.map((prof: any) => (
                    <label
                      key={prof.id}
                      className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                        selectedProfIds.includes(prof.id)
                          ? "border-primary/60 bg-primary/5"
                          : "border-border/50 hover:border-border"
                      }`}
                    >
                      <Checkbox
                        checked={selectedProfIds.includes(prof.id)}
                        onCheckedChange={() => toggleProf(prof.id)}
                        className="shrink-0"
                      />
                      {prof.avatarUrl ? (
                        <img src={prof.avatarUrl} alt={prof.name} className="w-5 h-5 rounded-full object-cover shrink-0" />
                      ) : (
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: prof.color ?? "#C9A96E" }} />
                      )}
                      <span className="text-sm truncate">{prof.name}</span>
                    </label>
                  ))}
                </div>
              )}
              {selectedProfIds.length > 0 && (
                <p className="text-xs text-primary mt-1.5">
                  {selectedProfIds.length} profissional{selectedProfIds.length > 1 ? "is" : ""} selecionado{selectedProfIds.length > 1 ? "s" : ""}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={isPending} className="bg-primary text-primary-foreground">
              {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {editId ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
